import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { captureAPI } from '../services/api';
import { findDocTypeSuggestions } from '../utils/docTypeSuggestions';
import DepartmentSelector from '../components/template-editor/DepartmentSelector';
import DocTypeEditModal from '../components/template-editor/DocTypeEditModal';
import DocTypeMatchList from '../components/template-editor/DocTypeMatchList';
import './DocumentTypesSettingsPage.css';

const CATEGORIES = ['Financial', 'Procurement', 'Legal', 'HR', 'General'];

const CATEGORY_ICONS = {
  Financial:   '💰',
  Procurement: '📦',
  Legal:       '⚖️',
  HR:          '👤',
  General:     '📄',
};

export default function DocumentTypesSettingsPage() {
  const [docTypes, setDocTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingPhrases, setEditingPhrases] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDocType, setNewDocType] = useState({ label: '', category: 'General', icon: '📄', primary_dept_key: '', detection_phrases: '' });
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  // Full-edit modal (Feature C) — now a shared DocTypeEditModal.
  const [editDt, setEditDt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [typesRes, deptsRes] = await Promise.all([
        captureAPI.getDocumentTypes(),
        captureAPI.getDepartments(),
      ]);
      setDocTypes(typesRes?.documentTypes || []);
      setDepartments((deptsRes?.departments || []).filter(d => d.is_active));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load document types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Similar existing types for the label being typed. A warning only: Create
  // stays available ("Create anyway") unless the label's key already exists,
  // which the unique (tenant_id, key) would refuse, or the label has no letter
  // or digit, which the server refuses.
  const suggestions = useMemo(
    () => findDocTypeSuggestions({ label: newDocType.label, docTypes }),
    [newDocType.label, docTypes],
  );
  const addBlocked = suggestions.keyEmpty || !!suggestions.collision;

  const deptLabel = (key) => departments.find(d => d.key === key)?.label || key;

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = docTypes.filter(d => d.category === cat);
    return acc;
  }, {});
  // Catch any uncategorized
  const otherCats = [...new Set(docTypes.filter(d => !CATEGORIES.includes(d.category)).map(d => d.category))];
  otherCats.forEach(cat => { grouped[cat] = docTypes.filter(d => d.category === cat); });

  const handleToggleActive = async (dt) => {
    setSaving(dt.id);
    try {
      await captureAPI.updateDocumentType(dt.id, { is_active: !dt.is_active });
      setDocTypes(prev => prev.map(d => d.id === dt.id ? { ...d, is_active: !d.is_active } : d));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  const handleStartEditPhrases = (dt) => {
    setEditingId(dt.id);
    setEditingPhrases((dt.detection_phrases || []).join(', '));
  };

  const handleSavePhrases = async (dt) => {
    const phrases = editingPhrases
      .split(',')
      .map(p => p.trim().toLowerCase())
      .filter(Boolean);
    // Clearing the box would blank the list, which is the same defect as
    // creating a type without one: the classifier would stop matching this type
    // entirely. The server refuses it; this says so without the round trip.
    if (phrases.length === 0) {
      setError(`"${dt.label}" needs at least one detection keyword — clearing them would stop it being recognised automatically.`);
      return;
    }
    setSaving(dt.id);
    setError(null);
    try {
      await captureAPI.updateDocumentType(dt.id, { detection_phrases: phrases });
      setDocTypes(prev => prev.map(d => d.id === dt.id ? { ...d, detection_phrases: phrases } : d));
      setEditingId(null);
    } catch (err) {
      // `details` carries the server's explanation for phrases_required; `error`
      // is the bare code. Preferring details keeps the reason visible instead of
      // showing the user the string "phrases_required".
      const data = err?.response?.data;
      setError(data?.details || data?.error || 'Failed to save keywords');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (dt) => {
    if (dt.is_system) return;
    if (!window.confirm(`Delete document type "${dt.label}"? This cannot be undone.`)) return;
    setSaving(dt.id);
    try {
      await captureAPI.deleteDocumentType(dt.id);
      setDocTypes(prev => prev.filter(d => d.id !== dt.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete');
    } finally {
      setSaving(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newDocType.label) { setAddError('Label is required'); return; }
    if (addBlocked) return;
    // Normalised the same way the server does, so the check here and the check
    // there agree on what "empty" means (', , ' is empty).
    const phrases = newDocType.detection_phrases
      .split(',')
      .map(p => p.trim().toLowerCase())
      .filter(Boolean);
    if (phrases.length === 0) {
      // A type with no keywords is never matched by the classifier — the server
      // refuses it. Caught here so the form says so before a round trip.
      setAddError('At least one detection keyword is required — without one this type can never be recognised automatically.');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      const res = await captureAPI.createDocumentType({
        label: newDocType.label,
        category: newDocType.category,
        icon: newDocType.icon,
        primary_dept_key: newDocType.primary_dept_key || null,
        detection_phrases: phrases,
      });
      setDocTypes(prev => [...prev, res.documentType]);
      setNewDocType({ label: '', category: 'General', icon: '📄', primary_dept_key: '', detection_phrases: '' });
      setShowAddForm(false);
    } catch (err) {
      // Prefer `details` over `error` for the same reason as handleSavePhrases:
      // the server's phrases_required response puts the explanation in details
      // and the bare code in error.
      const data = err?.response?.data;
      setAddError(data?.details || data?.error || 'Failed to create document type');
    } finally {
      setAdding(false);
    }
  };

  // ── Full edit (Feature C): now via the shared DocTypeEditModal ──
  const openEdit = (dt) => setEditDt(dt);

  const allCats = [...CATEGORIES, ...otherCats];
  const systemCount = docTypes.filter(d => d.is_system).length;
  const customCount = docTypes.filter(d => !d.is_system).length;

  if (loading) return <div className="dts-page"><p className="dts-loading">Loading document types…</p></div>;

  return (
    <div className="dts-page">
      <div className="dts-header">
        <div>
          <h2>Document Types</h2>
          <p className="dts-subtitle">
            Define the document types the system can recognize. Detection keywords drive automatic
            classification: when a document is captured, its text is scored against the keywords
            below and the best-matching active type wins. Edits here apply to the next document
            processed.
          </p>
          <p className="dts-subtitle">
            A type with no detection keywords is never matched automatically — it can still be
            assigned by hand or by a template, but automatic classification will never choose it.
          </p>
        </div>
        <button className="dts-add-btn" onClick={() => setShowAddForm(v => !v)}>
          {showAddForm ? 'Cancel' : '+ Add Document Type'}
        </button>
      </div>

      {error && (
        <div className="dts-error">
          {error}
          <button className="dts-error-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {showAddForm && (
        <form className="dts-add-form" onSubmit={handleAdd}>
          <div className="dts-add-row">
            <div className="dts-add-field">
              <label>Icon</label>
              <input
                type="text"
                value={newDocType.icon}
                onChange={e => setNewDocType(p => ({ ...p, icon: e.target.value }))}
                maxLength={2}
                className="dts-icon-input"
              />
            </div>
            <div className="dts-add-field" style={{ flex: 2 }}>
              <label>Label</label>
              <input
                type="text"
                placeholder="e.g. Work Order"
                value={newDocType.label}
                onChange={e => setNewDocType(p => ({ ...p, label: e.target.value }))}
                required
                autoFocus
              />
              <span className="dts-add-hint">
                Key: <code>{newDocType.label ? newDocType.label.toLowerCase().replace(/[^a-z0-9\s]/g,'').trim().replace(/\s+/g,'_') || '…' : '…'}</code>
              </span>
              <DocTypeMatchList
                suggestions={suggestions}
                inactiveHint="Inactive types can be turned on in the list below."
              />
            </div>
            <div className="dts-add-field" style={{ flex: 2 }}>
              <label>Department</label>
              <DepartmentSelector
                value={newDocType.primary_dept_key}
                onChange={key => setNewDocType(p => ({ ...p, primary_dept_key: key || '' }))}
                departments={departments}
                label={null}
              />
            </div>
            <div className="dts-add-field">
              <label className="dts-label-secondary">Category</label>
              <select value={newDocType.category} onChange={e => setNewDocType(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="dts-add-field" style={{ flex: 3 }}>
              <label>Detection Keywords (comma-separated)</label>
              <input
                type="text"
                placeholder="e.g. work order, wo #, job number"
                value={newDocType.detection_phrases}
                onChange={e => setNewDocType(p => ({ ...p, detection_phrases: e.target.value }))}
              />
              <span className="dts-add-hint">
                Required. Keywords are how documents of this type are recognised
                automatically — a type with none is never matched.
              </span>
            </div>
            <button type="submit" className="dts-create-btn" disabled={adding || addBlocked}>
              {adding ? 'Creating…' : suggestions.matches.length > 0 && !addBlocked ? 'Create anyway' : 'Create'}
            </button>
          </div>
          {addError && <p className="dts-add-error">{addError}</p>}
        </form>
      )}

      <div className="dts-categories">
        {allCats.map(cat => {
          const types = grouped[cat] || [];
          if (types.length === 0) return null;
          return (
            <div key={cat} className="dts-category">
              <h3 className="dts-category-title">
                <span>{CATEGORY_ICONS[cat] || '📄'} {cat}</span>
                <span className="dts-category-count">{types.length}</span>
              </h3>
              <table className="dts-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Active</th>
                    <th style={{ width: 40 }}></th>
                    <th>Label</th>
                    <th style={{ width: 150 }}>Department</th>
                    <th>Key</th>
                    <th>Detection Keywords</th>
                    <th style={{ width: 70 }}>System</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map(dt => (
                    <tr key={dt.id} className={[
                      'dts-row',
                      !dt.is_active ? 'dts-inactive' : '',
                      saving === dt.id ? 'dts-saving' : '',
                    ].filter(Boolean).join(' ')}>
                      <td className="dts-td-check">
                        <input
                          type="checkbox"
                          checked={dt.is_active}
                          onChange={() => handleToggleActive(dt)}
                          disabled={saving === dt.id}
                        />
                      </td>
                      <td className="dts-td-icon">{dt.icon || '📄'}</td>
                      <td className="dts-td-label">{dt.label}</td>
                      <td className="dts-td-dept">
                        {dt.primary_dept_key
                          ? <span className="dts-dept-tag">{deptLabel(dt.primary_dept_key)}</span>
                          : <span className="dts-no-phrases">—</span>}
                      </td>
                      <td><code className="dts-key">{dt.key}</code></td>
                      <td>
                        {editingId === dt.id ? (
                          <div className="dts-phrase-edit">
                            <input
                              className="dts-phrase-input"
                              value={editingPhrases}
                              onChange={e => setEditingPhrases(e.target.value)}
                              placeholder="keyword1, keyword2, keyword3"
                              autoFocus
                            />
                            <button className="dts-save-btn" onClick={() => handleSavePhrases(dt)} disabled={saving === dt.id}>
                              {saving === dt.id ? '…' : 'Save'}
                            </button>
                            <button className="dts-cancel-btn" onClick={() => setEditingId(null)}>Cancel</button>
                          </div>
                        ) : (
                          <div
                            className={`dts-phrases ${!dt.is_system ? 'dts-phrases-editable' : ''}`}
                            onClick={() => { if (!dt.is_system) handleStartEditPhrases(dt); }}
                            title={dt.is_system
                              ? 'System keywords (read-only here — use Edit)'
                              : 'Click to edit keywords. Without keywords this type is never matched automatically.'}
                          >
                            {(dt.detection_phrases || []).length > 0
                              ? (dt.detection_phrases || []).slice(0, 6).map((p, i) => (
                                  <span key={i} className="dts-phrase-tag">{p}</span>
                                ))
                              : <span className="dts-no-phrases">{dt.is_system ? 'No keywords — never auto-matched' : '+ Add keywords — never auto-matched until you do'}</span>
                            }
                            {(dt.detection_phrases || []).length > 6 && (
                              <span className="dts-phrase-more">+{(dt.detection_phrases || []).length - 6} more</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {dt.is_system && <span className="dts-system-badge">system</span>}
                      </td>
                      <td className="dts-td-actions">
                        <button
                          className="dts-edit-btn"
                          onClick={() => openEdit(dt)}
                          disabled={saving === dt.id}
                          title={dt.is_system ? 'System type — only Active can be changed' : 'Edit document type'}
                        >
                          Edit
                        </button>
                        {!dt.is_system && (
                          <button
                            className="dts-delete-btn"
                            onClick={() => handleDelete(dt)}
                            disabled={saving === dt.id}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      <div className="dts-footer">
        <span>{systemCount} system types</span>
        <span>·</span>
        <span>{customCount} custom types</span>
        <span>·</span>
        <span>{docTypes.filter(d => d.is_active).length} active</span>
      </div>

      {editDt && (
        <DocTypeEditModal
          docType={editDt}
          departments={departments}
          categories={CATEGORIES}
          onClose={() => setEditDt(null)}
          onSaved={(payload) => {
            setDocTypes(prev => prev.map(d => d.id === editDt.id ? { ...d, ...payload } : d));
            setEditDt(null);
          }}
        />
      )}
    </div>
  );
}
